import React from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { SalaryHistoryDbType } from '../../../../api/types/accountants/salary-history/salary-history.type';
import { LegalEntityType } from '../../../user-page/roles/lecturer-price-rate/componenets/acountant/user-page-lecturer-rate-accountant';
import { LecturerRateDisplayType } from '../../../../api/types/lecturers/price-rate/lecturer-rate-display-type';

interface SalaryExportDialogProps {
  visible: boolean;
  onHide: () => void;
  selectedData: SalaryHistoryDbType[];
}

interface AggregatedUserData {
  nationalId: string;
  firstName: string;
  lastName: string;
  entityType: string;
  detailedEntityTypes: string;
  reportingCodes: string;
  legalEntityIds: string;
  totalHours: number;
  totalRate: number;
  totalAmount: number;
}

export const SalaryExportDialog: React.FC<SalaryExportDialogProps> = ({
  visible,
  onHide,
  selectedData,
}) => {
  const calculateHours = (startAt: Date, endAt: Date): number => {
    const start = new Date(startAt);
    const end = new Date(endAt);
    const diffInMs = end.getTime() - start.getTime();
    const diffInHours = diffInMs / (1000 * 60 * 60);
    return Math.max(0, diffInHours);
  };

  const getEntityTypeDisplayName = (entityType: LegalEntityType): string => {
    switch (entityType) {
      case LegalEntityType.EMPLOYEE:
        return 'שכיר';
      case LegalEntityType.VAT_REGISTERED_FREELANCER:
      case LegalEntityType.VAT_EXEMPT_FREELANCER:
        return 'עצמאי';
      case LegalEntityType.COMPANY:
      case LegalEntityType.NONPROFIT:
        return 'חברה';
      default:
        return 'לא ידוע';
    }
  };

  const filterAndAggregateData = (
    targetEntityTypes: LegalEntityType[],
  ): AggregatedUserData[] => {
    const userMap = new Map<string, AggregatedUserData>();

    selectedData.forEach((salary) => {
      salary.items.forEach((item) => {
        if (targetEntityTypes.includes(item.legalEntityType)) {
          const key = salary.nationalId;
          const hours = calculateHours(item.startAt, item.endAt);
          const amount =
            hours *
            item.rate *
            (item.displayType === LecturerRateDisplayType.OneHour ? 1 : 4 / 3);

          if (userMap.has(key)) {
            const existing = userMap.get(key)!;
            existing.totalHours += hours;
            existing.totalAmount += amount;
            // Calculate effective rate as totalAmount / totalHours
            existing.totalRate =
              existing.totalHours > 0
                ? existing.totalAmount / existing.totalHours
                : 0;

            // Collect unique detailed entity types
            const currentDetailedTypes = (existing.detailedEntityTypes || '')
              .split(', ')
              .filter((t) => t);
            const newDetailedType = LegalEntityType.getDisplayText(
              item.legalEntityType,
            );
            if (!currentDetailedTypes.includes(newDetailedType)) {
              existing.detailedEntityTypes =
                currentDetailedTypes.length > 0
                  ? `${existing.detailedEntityTypes}, ${newDetailedType}`
                  : newDetailedType;
            }

            // Collect unique reporting codes
            const currentCodes = (existing.reportingCodes || '')
              .split(', ')
              .filter((c) => c);
            if (
              item.reportingCode &&
              !currentCodes.includes(item.reportingCode)
            ) {
              existing.reportingCodes =
                currentCodes.length > 0
                  ? `${existing.reportingCodes}, ${item.reportingCode}`
                  : item.reportingCode;
            }

            // Collect unique legal entity IDs
            const currentIds = (existing.legalEntityIds || '')
              .split(', ')
              .filter((id) => id);
            if (
              item.legalEntityId &&
              !currentIds.includes(item.legalEntityId)
            ) {
              existing.legalEntityIds =
                currentIds.length > 0
                  ? `${existing.legalEntityIds}, ${item.legalEntityId}`
                  : item.legalEntityId;
            }
          } else {
            userMap.set(key, {
              nationalId: salary.nationalId,
              firstName: salary.firstName,
              lastName: salary.lastName,
              entityType: getEntityTypeDisplayName(item.legalEntityType),
              detailedEntityTypes: LegalEntityType.getDisplayText(
                item.legalEntityType,
              ),
              reportingCodes: item.reportingCode || '',
              legalEntityIds: item.legalEntityId || '',
              totalHours: hours,
              totalRate: hours > 0 ? item.rate : 0,
              totalAmount: amount,
            });
          }
        }
      });
    });

    return Array.from(userMap.values());
  };

  const generateExcel = (entityTypes: LegalEntityType[], fileName: string) => {
    const aggregatedData = filterAndAggregateData(entityTypes);

    if (aggregatedData.length === 0) {
      alert('אין נתונים להצגה עבור הקטגוריה שנבחרה');
      return;
    }

    const headers = [
      'מספר זהות',
      'שם פרטי',
      'שם משפחה',
      'סוג יישות',
      'סוג ישות משפטית מפורט',
      'קוד דיווח במערכת שכר',
      'מזהה ישות משפטית',
      'כמות (שעות)',
      'תעריף שעתי (60 דק)',
      'סה״כ',
    ];

    const rows = aggregatedData.map((user) => [
      user.nationalId,
      user.firstName,
      user.lastName,
      user.entityType,
      user.detailedEntityTypes,
      user.reportingCodes,
      user.legalEntityIds,
      user.totalHours.toFixed(2),
      user.totalRate.toFixed(2),
      user.totalAmount.toFixed(2),
    ]);

    const excelData = [headers, ...rows];

    // Create a custom version of the excel generator with custom filename
    const generateCustomExcel = (
      data: string[][],
      customFileName: string,
    ): void => {
      import('xlsx').then((XLSX) => {
        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.aoa_to_sheet(data);

        XLSX.utils.book_append_sheet(workbook, worksheet, 'נתוני שכר');

        const binaryString = XLSX.write(workbook, {
          bookType: 'xlsx',
          type: 'binary',
        });

        const buffer = new ArrayBuffer(binaryString.length);
        const view = new Uint8Array(buffer);
        for (let i = 0; i < binaryString.length; i++) {
          view[i] = binaryString.charCodeAt(i) & 0xff;
        }

        const blob = new Blob([buffer], { type: 'application/octet-stream' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = customFileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      });
    };

    generateCustomExcel(excelData, fileName);
    onHide();
  };

  const handleEmployeeExport = () => {
    generateExcel([LegalEntityType.EMPLOYEE], 'שכירים.xlsx');
  };

  const handleFreelancerExport = () => {
    generateExcel(
      [
        LegalEntityType.VAT_REGISTERED_FREELANCER,
        LegalEntityType.VAT_EXEMPT_FREELANCER,
      ],
      'עצמאים.xlsx',
    );
  };

  const handleCompanyExport = () => {
    generateExcel(
      [LegalEntityType.COMPANY, LegalEntityType.NONPROFIT],
      'חברות.xlsx',
    );
  };

  return (
    <Dialog
      header="יצוא נתוני שכר לאקסל"
      visible={visible}
      onHide={onHide}
      style={{ width: '450px', direction: 'rtl' }}
      footer={
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-start',
            gap: '0.5rem',
          }}
        >
          <Button
            label="ביטול"
            icon="pi pi-times"
            onClick={onHide}
            className="p-button-text"
          />
        </div>
      }
    >
      <div style={{ textAlign: 'right' }}>
        <p style={{ marginBottom: '1.5rem', color: '#6B7280' }}>
          בחר את סוג הישות לייצוא נתוני השכר:
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <Button
            label="שכירים"
            icon="pi pi-users"
            onClick={handleEmployeeExport}
            style={{
              backgroundColor: '#3B82F6',
              border: 'none',
              padding: '1rem',
              fontSize: '1rem',
              borderRadius: '0.5rem',
            }}
          />

          <Button
            label="עצמאים"
            icon="pi pi-user"
            onClick={handleFreelancerExport}
            style={{
              backgroundColor: '#10B981',
              border: 'none',
              padding: '1rem',
              fontSize: '1rem',
              borderRadius: '0.5rem',
            }}
          />

          <Button
            label="חברות"
            icon="pi pi-building"
            onClick={handleCompanyExport}
            style={{
              backgroundColor: '#8B5CF6',
              border: 'none',
              padding: '1rem',
              fontSize: '1rem',
              borderRadius: '0.5rem',
            }}
          />
        </div>

        <div
          style={{
            marginTop: '1.5rem',
            padding: '1rem',
            backgroundColor: '#F3F4F6',
            borderRadius: '0.5rem',
          }}
        >
          <p style={{ margin: 0, fontSize: '0.875rem', color: '#6B7280' }}>
            <strong>נבחרו:</strong> {selectedData.length} רשומות משכורת
          </p>
        </div>
      </div>
    </Dialog>
  );
};
